import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";

const PHONE = "31616448413";
const DISPLAY_NAME = "Zemar | SalesCoach";
const SUBTITLE = "Reactie binnen enkele uren";
const AVATAR_SRC = "/assets/ZRSalesCoach-ProfielV1-420.webp";

const QUICK_QUESTIONS = [
  "Wat kost een salescoaching traject?",
  "Hoe lang duurt een traject?",
  "Kan ik een gratis gesprek inplannen?",
  "Wat zijn de resultaten van jullie klanten?",
];

function buildWaUrl(text: string) {
  return `https://wa.me/${PHONE}?text=${encodeURIComponent(text)}`;
}

export default function WhatsAppWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return null;

  const handleSend = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    window.open(buildWaUrl(trimmed), "_blank", "noopener,noreferrer");
    setInput("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend(input);
  };

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  return (
    <>
      {/* Backdrop on mobile */}
      {open && (
        <div
          className="fixed inset-0 z-40 sm:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <div className="fixed bottom-5 left-4 z-50 flex flex-col items-start gap-3">
        {/* Chat popup */}
        {open && (
          <div
            className="w-[300px] sm:w-[320px] rounded-2xl overflow-hidden shadow-2xl border border-white/10"
            style={{ background: "#e5ddd5" }}
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 px-4 py-3"
              style={{ background: "#075e54" }}
            >
              <div
                className="w-10 h-10 rounded-full overflow-hidden shrink-0 border-2 border-white/30"
                style={{ background: "#25d366" }}
              >
                <img
                  src={AVATAR_SRC}
                  alt="Zemar"
                  className="w-full h-full object-cover object-top"
                  draggable={false}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm leading-tight truncate">
                  {DISPLAY_NAME}
                </p>
                <p className="text-white/70 text-xs leading-tight">{SUBTITLE}</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white transition-colors ml-1 shrink-0"
                aria-label="Sluiten"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Phone chat body */}
            <div className="px-3 pt-3 pb-2" style={{ background: "#e5ddd5" }}>
              {/* Greeting bubble */}
              <div className="flex justify-start mb-3">
                <div
                  className="rounded-2xl rounded-tl-sm px-3 py-2 text-sm shadow-sm max-w-[90%]"
                  style={{ background: "#ffffff", color: "#111" }}
                >
                  <p className="font-medium text-xs mb-0.5" style={{ color: "#075e54" }}>
                    {DISPLAY_NAME}
                  </p>
                  <p>Waarmee kan ik je helpen? 👋</p>
                  <p className="text-[10px] text-right mt-1" style={{ color: "#999" }}>nu</p>
                </div>
              </div>

              {/* Quick question chips */}
              <div className="flex flex-col gap-1.5 mb-3">
                {QUICK_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => handleSend(q)}
                    className="text-left text-sm px-3 py-2 rounded-xl border transition-colors hover:opacity-90 active:opacity-70"
                    style={{
                      background: "#ffffff",
                      borderColor: "#25d366",
                      color: "#075e54",
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Input bar */}
            <div
              className="flex items-center gap-2 px-3 py-2 border-t"
              style={{ background: "#f0f0f0", borderColor: "#ddd" }}
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Typ een bericht..."
                className="flex-1 bg-white rounded-full px-3 py-1.5 text-sm outline-none border border-gray-200 placeholder:text-gray-400"
              />
              <button
                onClick={() => handleSend(input)}
                disabled={!input.trim()}
                aria-label="Verstuur"
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-opacity disabled:opacity-40"
                style={{ background: "#25d366" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* FAB button */}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Sluit WhatsApp chat" : "Open WhatsApp chat"}
          className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
          style={{ background: "#25d366" }}
        >
          {open ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg viewBox="0 0 32 32" width="28" height="28" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.003 2.667C8.637 2.667 2.667 8.636 2.667 16c0 2.363.641 4.675 1.857 6.693L2.667 29.333l6.838-1.792A13.28 13.28 0 0 0 16.003 29.333C23.368 29.333 29.333 23.364 29.333 16S23.368 2.667 16.003 2.667zm0 24.267a11.015 11.015 0 0 1-5.613-1.543l-.403-.24-4.183 1.097 1.115-4.073-.261-.415A11.01 11.01 0 0 1 5 16c0-6.065 4.937-11 11.003-11C23.065 5 28 9.935 28 16s-4.935 11-11.997 11zm6.04-8.233c-.33-.166-1.958-.965-2.261-1.075-.303-.11-.523-.165-.743.166-.22.33-.853 1.075-.988 1.295-.135.22-.27.248-.501.083-.33-.166-1.396-.515-2.659-1.64-1.002-.872-1.557-1.98-1.738-2.31-.182-.33-.019-.508.136-.673.14-.14.33-.358.495-.537.166-.18.22-.303.33-.504.11-.22.055-.413-.028-.578-.083-.166-.743-1.789-1.018-2.45-.268-.642-.54-.555-.743-.565l-.633-.011c-.22 0-.578.083-.88.413-.303.33-1.155 1.129-1.155 2.752s1.183 3.193 1.348 3.413c.165.22 2.328 3.556 5.642 4.988.789.34 1.405.543 1.886.695.793.25 1.515.215 2.085.13.636-.094 1.958-.8 2.234-1.572.274-.77.274-1.43.192-1.57-.083-.138-.303-.22-.633-.385z"/>
            </svg>
          )}
        </button>
      </div>
    </>
  );
}
